import { useRef, useMemo, useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ContentEditable from "react-contenteditable";
import { Image, message, Tooltip, Upload, UploadProps } from "antd";

import GuideVideo from "components/guide-video/guide-video";
import { Guides, Guide_Steps } from "graphql/generated/graphql";
import CommentPopup, { Comment } from "components/comment-popup/comment-popup";
import SignUpModal from "components/sign-up-modal/sign-up-modal";
import { useAddGuideCommentMutation } from "graphql/mutations/add-guide-comment.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";

import { RcFile } from "antd/es/upload";
import { convertHeicFileToJpeg } from "utils/files-management";
import { useBrand } from "context/BrandContext";
import useScreenSize from "hooks/useScreenSize";

interface BaseGuideStepProps {
	guide: Guides;
	step: Guide_Steps & {
		image_file?: RcFile; // only used in editing mode
		video_file?: RcFile;
	};
	stepNumber: number;
}

interface EditingGuideStepProps extends BaseGuideStepProps {
	isUserEditing: true;
	onDelete: () => void;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
	onMediaChange?: (media: { image_file?: RcFile; video_file?: RcFile }, removeMedia?: boolean) => void;
	comments?: never;
}

interface NonEditingGuideStepProps extends BaseGuideStepProps {
	isUserEditing?: false;
	onDelete?: () => void;
	onTitleChange?: never;
	onDescriptionChange?: never;
	onMediaChange?: never;
	comments: Partial<Comment>[];
}

type GuideStepProps = EditingGuideStepProps | NonEditingGuideStepProps;

const GuideStep = ({
	guide,
	comments = [],
	step,
	stepNumber,
	isUserEditing,
	onDelete,
	onTitleChange,
	onDescriptionChange,
	onMediaChange
}: GuideStepProps) => {
	const [mediaPreview, setMediaPreview] = useState<string | null>(null);
	const [isHovered, setIsHovered] = useState(false);
	const [showCommentPopup, setShowCommentPopup] = useState(false);
	const [showSignUpModal, setShowSignUpModal] = useState(false);
	const commentIconRef = useRef<HTMLButtonElement>(null);
	const mobileCommentIconRef = useRef<HTMLButtonElement>(null);
	const { loggedUserValue, isUserAnon } = useLoggedUser();
	const { theme } = useBrand();
	const screenSize = useScreenSize();

	const { id, title, body, start_time, end_time, image_url, video_url, image_file, video_file } = step || {};

	const titleRef = useRef<HTMLDivElement>(null);
	const descriptionRef = useRef<HTMLDivElement>(null);

	// Centralized media state logic
	const mediaState = useMemo(() => {
		const hasAiVideo = end_time !== null && start_time !== null;
		const hasExistingMedia = Boolean(image_url || video_url);
		const hasPreviewMedia = Boolean(image_file || video_file);

		return {
			hasAiVideo,
			hasExistingMedia,
			hasPreviewMedia,
			// View mode conditions
			// showAiVideoInView: !isUserEditing && hasAiVideo,
			// showCustomMediaInView: !isUserEditing && !hasAiVideo && hasExistingMedia,
			showAiVideoInView: hasAiVideo,
			showCustomMediaInView: !hasAiVideo && hasExistingMedia,
			// Edit mode conditions
			showUploadPlaceholder: isUserEditing && !hasAiVideo && !hasExistingMedia && !hasPreviewMedia,
			showCustomMediaInEdit: isUserEditing && !hasAiVideo && (hasExistingMedia || hasPreviewMedia)
		};
	}, [isUserEditing, start_time, end_time, image_url, video_url, image_file, video_file]);

	// Parse the description and return React elements or HTML string
	const descriptionContent = useMemo(() => {
		if (!body) return { isArray: false, content: "" };

		// Case 1: Trying to parse as JSON array
		try {
			const parsed = JSON.parse(body);
			if (Array.isArray(parsed)) {
				// If we are editing, return HTML string for ContentEditable
				if (isUserEditing) {
					const bulletList = parsed.map(item => `<li class="guide-step-instruction-text">${item}</li>`).join("");
					return {
						isArray: false,
						content: `<ul class="guide-step-list">${bulletList}</ul>`
					};
				}

				// If we are NOT editing, return React elements
				return {
					isArray: true,
					content: (
						<ul className="guide-step-list">
							{parsed.map((item, index) => (
								<li key={index} className="guide-step-instruction-text">
									{item}
								</li>
							))}
						</ul>
					)
				};
			}
		} catch (error) {
			// This is not a valid JSON, continue with the next case.
		}

		// Case 2: Check if it is already HTML with <ul> and <li>
		if (body.trim().startsWith("<ul>") || body.trim().startsWith("<ul ")) {
			// If we are editing, return the HTML as is
			if (isUserEditing) {
				return { isArray: false, content: body };
			}

			// If we are NOT editing, parse the HTML and convert to React elements
			const parser = new DOMParser();
			const doc = parser.parseFromString(body, "text/html");
			const ulElement = doc.querySelector("ul");

			if (ulElement) {
				const items = Array.from(ulElement.querySelectorAll("li")).map(li => li.textContent || "");

				return {
					isArray: true,
					content: (
						<ul className="guide-step-list">
							{items.map((item, index) => (
								<li key={index} className="guide-step-instruction-text">
									{item}
								</li>
							))}
						</ul>
					)
				};
			}
		}

		// Case 3: Simple text or different HTML
		return { isArray: false, content: body };
	}, [body, isUserEditing]);

	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		...(isUserEditing ? { marginRight: 0 } : {})
	};

	const handleDelete = () => {
		if (onDelete) {
			onDelete();
		}
	};

	const handleFileRemove = () => {
		onMediaChange && onMediaChange({ image_file: undefined, video_file: undefined }, true);
		message.info("File removed");
	};

	const uploadProps: UploadProps = {
		accept: "video/*,image/*",
		multiple: false,
		maxCount: 1,
		beforeUpload: async file => {
			if (!onMediaChange) return false;

			let processedFile: RcFile = file;
			if (/\.(heic|heif)$/i.test(file.name) || file.type === "image/heic" || file.type === "image/heif") {
				try {
					const converted = await convertHeicFileToJpeg(file);
					processedFile = Object.assign(converted, { uid: file.uid }) as RcFile;
				} catch {
					message.error("Failed to convert HEIC image.");
					return false;
				}
			}

			if (processedFile.type.startsWith("image/")) {
				onMediaChange({ image_file: processedFile });
			} else if (processedFile.type.startsWith("video/")) {
				const isLt2GB = processedFile.size <= 2 * 1024 * 1024 * 1024; // allow files up to 2GB

				if (!isLt2GB) {
					message.error("Video must be smaller than 2GB!");
					return false;
				}

				onMediaChange({ video_file: processedFile });
			} else {
				message.error("You can only upload video or image files!");
			}

			return false; // Prevent default upload behavior
		},
		onRemove: handleFileRemove,
		showUploadList: false // We'll handle the preview ourselves
	};

	// Create preview URL when video changes
	useEffect(() => {
		const previewMedia = video_file || image_file;
		if (previewMedia && previewMedia instanceof File) {
			const previewUrl = URL.createObjectURL(previewMedia);
			setMediaPreview(previewUrl);

			// Cleanup function to revoke URL when component unmounts or video changes
			return () => {
				URL.revokeObjectURL(previewUrl);
			};
		} else {
			setMediaPreview(null);
		}
	}, [video_file, image_file]);

	const [addGuideComment, { loading: addingComment }] = useAddGuideCommentMutation();

	const stepComments = useMemo(() => {
		if (isUserEditing) return [];

		return comments.filter(comment => comment.step_number === stepNumber);
	}, [comments, stepNumber, isUserEditing]);

	const handleAddComment = async (text: string) => {
		if (isUserEditing) return;

		try {
			await addGuideComment({
				variables: {
					comment: {
						guide_id: guide.id,
						step_number: stepNumber,
						comment: text,
						created_by_user_id: loggedUserValue?.id
					}
				},
				refetchQueries: ["getGuideComments"],
				awaitRefetchQueries: true
			});
			message.success("Comment added successfully!");
		} catch (error) {
			console.error("Failed to add comment:", error);
			message.error("Failed to add comment. Please try again.");
		}
	};

	// Centralized media rendering logic
	const renderMedia = () => {
		if (mediaState.showAiVideoInView) {
			return (
				<div className="guide-step-video">
					<GuideVideo videoUrl={guide.video_url || ""} startTime={start_time} endTime={end_time} />
				</div>
			);
		}

		if (mediaState.showCustomMediaInView) {
			return (
				<div className="guide-step-media">
					{video_url ? (
						<video src={video_url} controls className="guide-step-custom-media-video" />
					) : (
						<img src={image_url || ""} alt="Step Media" className="guide-step-custom-media-image" />
					)}
				</div>
			);
		}

		if (mediaState.showUploadPlaceholder) {
			return (
				<div className="guide-step-video">
					<Upload {...uploadProps} className="guide-step-upload">
						<img src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`} alt="Add Media" />
					</Upload>
				</div>
			);
		}

		if (mediaState.showCustomMediaInEdit) {
			if (video_file || video_url) {
				return (
					<div className="guide-step-video">
						<GuideVideo videoUrl={video_url || mediaPreview || ""} />
					</div>
				);
			}

			return (
				<div className="guide-step-image">
					<div className="guide-step-media-remove" onClick={handleFileRemove}>
						<img src={`${process.env.PUBLIC_URL}/assets/images/icons/x-black.svg`} alt="Remove Media" />
					</div>
					<Image src={image_url || mediaPreview || ""} alt="Step Media" className="guide-step-custom-media-image" />
				</div>
			);
		}

		return null;
	};

	return (
		<div
			className="guide-step"
			ref={setNodeRef}
			style={style}
			{...attributes}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className="guide-step-header">
				<div className="guide-step-number-icon">
					<span>{stepNumber}</span>
				</div>
				<ContentEditable
					innerRef={titleRef}
					html={title}
					disabled={!isUserEditing}
					onChange={e => onTitleChange && onTitleChange(e.target.value)}
					tagName="span"
					className="guide-step-title"
				/>
				<div className="guide-step-edit-actions-container">
					{isUserEditing && (
						<>
							<button title="Delete" className="guide-step-edit-actions-button" onClick={handleDelete}>
								<img src={`${process.env.PUBLIC_URL}/assets/images/icons/trashcan.svg`} alt="Delete" />
							</button>
							<button title="Drag to reorder" className="guide-step-edit-actions-button" {...listeners}>
								<img
									src={`${process.env.PUBLIC_URL}/assets/images/icons/drag-icon.svg`}
									alt="Drag"
									style={{ margin: "2px 0" }}
								/>
							</button>
						</>
					)}
				</div>
				<button ref={mobileCommentIconRef} title="Comments" className="guide-step-mobile-comment-button">
					{stepComments.length > 0 && <span className="guide-step-comment-count">{stepComments.length}</span>}
					<Tooltip
						overlayClassName="guide-step-tooltip"
						color="black"
						placement="bottom"
						title="Leave a comment on this step"
						mouseLeaveDelay={0}
					>
						<img
							className="guide-step-comment-button-img"
							src={
								stepComments.length > 0
									? `${process.env.PUBLIC_URL}/assets/images/icons/message-text-square.svg`
									: `${process.env.PUBLIC_URL}${theme.commentIcon}`
							}
							onClick={() => {
								if (isUserAnon) {
									setShowSignUpModal(true);
								} else {
									setShowCommentPopup(!showCommentPopup);
								}
							}}
						/>
					</Tooltip>
				</button>
			</div>

			<div className="guide-step-content">
				<div className="guide-step-left">
					{isUserEditing || !descriptionContent.isArray ? (
						// Edit mode or plain text: use ContentEditable
						<ContentEditable
							innerRef={descriptionRef}
							html={descriptionContent.content as string}
							disabled={!isUserEditing}
							onChange={e => onDescriptionChange && onDescriptionChange(e.target.value)}
							className="guide-step-description"
						/>
					) : (
						// View mode (not edit) with array: render React elements
						<div className="guide-step-description">{descriptionContent.content}</div>
					)}
				</div>

				{renderMedia()}
			</div>

			{(isHovered || showCommentPopup || !!stepComments.length) && (
				<button ref={commentIconRef} title="Comments" className="guide-step-comment-button">
					{stepComments.length > 0 && <span className="guide-step-comment-count">{stepComments.length}</span>}
					<Tooltip
						overlayClassName="guide-step-tooltip"
						color="black"
						placement="bottom"
						title="Leave a comment on this step"
						mouseLeaveDelay={0}
					>
						<img
							className="guide-step-comment-button-img"
							src={
								stepComments.length > 0
									? `${process.env.PUBLIC_URL}/assets/images/icons/message-text-square.svg`
									: `${process.env.PUBLIC_URL}${theme.commentIcon}`
							}
							onClick={() => {
								if (isUserAnon) {
									setShowSignUpModal(true);
								} else {
									setShowCommentPopup(!showCommentPopup);
								}
							}}
						/>
					</Tooltip>
				</button>
			)}

			{showCommentPopup && !isUserAnon && (
				<CommentPopup
					comments={stepComments as Required<Comment>[]}
					onAddComment={handleAddComment}
					onClose={() => setShowCommentPopup(false)}
					anchorElement={screenSize.width < 768 ? mobileCommentIconRef.current : commentIconRef.current}
					loading={addingComment}
				/>
			)}

			<SignUpModal open={showSignUpModal} onClose={() => setShowSignUpModal(false)} />
		</div>
	);
};

export default GuideStep;
